<script setup>
import {
  ref,
  onMounted,
  computed,
  watchEffect,
  nextTick,
  watch,
  provide,
} from 'vue';
import { ShadowRoot } from 'vue-shadow-dom';

// Import comps
import Notifications from '@/components/utility/notifications/index.vue';
import LoadingIndicator from '@/components/utility/loading-indicator/index.ts';
import AdminMenu from '@/components/app/menu/index.vue';
import ToolBar from '@/components/app/toolbar/index.vue';

// Funcs
import { hexToRgb } from '@/assets/js/functions/hexToRgb.js';
import { isObject } from '@/assets/js/functions/isObject.js';
import {
  setupColorScheme,
  useColorScheme,
} from '@/assets/js/functions/useColorScheme.js';
import { ADOPTED_STYLE_SHEETS_KEY } from '@/composables/useAdoptedStyleSheets.js';
setupColorScheme();
const { prefersDark } = useColorScheme();

// Refs
const adoptedStyleSheets = ref(new CSSStyleSheet());

// Provide adoptedStyleSheets globally for child components
provide(ADOPTED_STYLE_SHEETS_KEY, adoptedStyleSheets);

// Store
import { useAppStore } from '@/store/app/app.js';
const appStore = useAppStore();

/**
 * Injects styles into shadow root
 */
const setStyles = () => {
  let appStyleNode = document.querySelector('#uixpress-css');
  if (!appStyleNode) {
    appStyleNode = manuallyAddStyleSheet();
    // Wait for stylesheet to load
    appStyleNode.onload = () => {
      const appStyles = appStyleNode.sheet;
      for (const rule of [...appStyles.cssRules].reverse()) {
        adoptedStyleSheets.value.insertRule(rule.cssText);
      }
      setCustomProperties();
    };
  } else {
    const appStyles = appStyleNode.sheet;
    for (const rule of [...appStyles.cssRules].reverse()) {
      adoptedStyleSheets.value.insertRule(rule.cssText);
    }
    setCustomProperties();
  }

  //appStyleNode.remove();
};

const manuallyAddStyleSheet = () => {
  var link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `${appStore.state.pluginBase}app/dist/assets/styles/app.css`;
  document.head.appendChild(link);

  return link;
};

const returnCustomCSS = computed(() => {
  return appStore.state.uixpress_settings?.custom_css || '';
});

const returnLayout = computed(() => {
  return appStore.state.uixpress_settings?.layout;
});

/**
 * Get current page identifier for magic dark mode
 * @returns {string} Page identifier (URL path or page parameter)
 */
const getCurrentPageIdentifier = () => {
  const url = new URL(window.location.href);
  const urlParams = new URLSearchParams(url.search);

  // If there's a 'page' parameter, use that (for plugin settings pages)
  if (urlParams.has('page')) {
    return urlParams.get('page');
  }

  // Otherwise use the pathname
  const pathname = url.pathname;
  const adminPath =
    appStore.state.adminUrl?.replace(window.location.origin, '') ||
    '/wp-admin/';

  // Extract the page filename from the path
  if (pathname.includes(adminPath)) {
    const relativePath = pathname.replace(adminPath, '');
    return relativePath || 'index.php';
  }

  // Fallback to full pathname
  return pathname;
};

/**
 * Check if current page is in the magic dark mode pages array
 * @returns {boolean} True if page should have magic dark mode
 */
const isCurrentPageInMagicDarkModeList = () => {
  const magicDarkModePages =
    appStore.state.uixpress_settings?.magic_dark_mode_pages || [];
  const currentPage = getCurrentPageIdentifier();
  return magicDarkModePages.includes(currentPage);
};

/**
 * Computed property to determine if magic dark mode should be enabled
 */
const shouldUseMagicDarkMode = computed(() => {
  // Only enable if dark mode is active and current page is in the magic dark mode pages list
  return prefersDark.value && isCurrentPageInMagicDarkModeList();
});

const setCustomProperties = () => {
  // Base colors
  if (Array.isArray(appStore.state.uixpress_settings.base_theme_scale)) {
    const baseCssValues = appStore.state.uixpress_settings.base_theme_scale;
    for (let color of baseCssValues) {
      const hexArray = hexToRgb(color.color);
      const variableName = `--uix-base-${color.step}`;
      document.documentElement.style.setProperty(
        variableName,
        hexArray.join(' ')
      );
    }
  }

  // Accent colors
  if (Array.isArray(appStore.state.uixpress_settings.accent_theme_scale)) {
    const baseCssValues = appStore.state.uixpress_settings.accent_theme_scale;

    for (let color of baseCssValues) {
      const hexArray = hexToRgb(color.color);
      const variableName = `--uix-accent-${color.step}`;
      document.documentElement.style.setProperty(
        variableName,
        hexArray.join(' ')
      );
    }
  }
};

const mountViewTransitions = async () => {
  // No view transition support
  if (!document.startViewTransition) return;

  let isNavigating = false;

  window.navigation.addEventListener('navigate', (event) => {
    const toUrl = new URL(event.destination.url);

    // Only handle navigate events for same-origin navigation
    if (location.origin !== toUrl.origin) return;

    // Don't intercept if it's a reload or if we're already navigating
    if (
      event.navigationType === 'reload' ||
      event.navigationType === 'replace' ||
      isNavigating
    )
      return;

    // Don't load on hash changes
    if (toUrl) {
      if (toUrl.hash) return;
    }

    appStore.updateState('loading', true);

    setTimeout(() => {
      appStore.updateState('loading', false);
    }, 5000);
  });
};

watchEffect(() => {
  if (prefersDark.value) {
    document.body.classList.add('dark');
  } else {
    document.body.classList.remove('dark');
  }
});

document.body.classList.add('uipc-body');

setStyles();
onMounted(mountViewTransitions);

window.addEventListener('pagehide', (event) => {
  appStore.updateState('loading', false);
});

// Watch for changes to the settings options and update accordingly
document.addEventListener('uipx-settings-update', (event) => {
  // Bail if not an object
  if (!isObject(event.detail)) return;
  // Update settings using updateState to ensure reactivity
  appStore.updateState('uixpress_settings', {
    ...appStore.state.uixpress_settings,
    ...event.detail,
  });
});

// Computed classes for admin menu
const menuContainerClasses = computed(() => {
  const baseClasses = 'shrink-0 uipx-normalize';

  switch (returnLayout.value) {
    case 'rounded  ':
      return `${baseClasses} bg-white dark:bg-zinc-900`;
    case 'bubble  ':
      return `${baseClasses} p-6 bg-zinc-100/50 dark:bg-zinc-900 max-h-[100dvh]`;
    default:
      return baseClasses;
  }
});

const menuClasses = computed(() => {
  const baseClasses = 'uipx-normalize box-border font-sans';
  const darkClass = prefersDark.value ? 'dark' : '';

  switch (returnLayout.value) {
    case 'rounded':
      return `${baseClasses} ${darkClass} max-h-screen h-screen`;
    case 'bubble':
      return `${baseClasses} ${darkClass} border border-zinc-100 dark:border-zinc-700/30 bg-white dark:bg-zinc-800/40 rounded-2xl shadow-lg z-[3] top-4 left-4 bottom-4 fixed overflow-hidden `;
    default:
      return `${baseClasses} ${darkClass} border-r border-zinc-200 dark:border-zinc-700/30 bg-white dark:bg-zinc-900 max-h-screen h-screen`;
  }
});

watch(
  () => prefersDark.value,
  (newVal) => {
    if (newVal) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  },
  { immediate: true }
);

watch(
  () => returnLayout.value,
  (newVal) => {
    if (newVal === 'rounded') {
      document.documentElement.classList.add('uixp-rounded');
    } else {
      document.documentElement.classList.remove('uixp-rounded');
    }
  },
  { immediate: true }
);

/**
 * Apply or remove magic dark mode class on #wpbody-content
 */
const applyMagicDarkModeClass = () => {
  nextTick(() => {
    const wpbodyContent = document.getElementById('wpbody-content');
    const wpcontent = document.getElementById('wpcontent');

    let wrapper = wpbodyContent || wpcontent;

    if (wrapper) {
      if (shouldUseMagicDarkMode.value) {
        wrapper.classList.add('dark-mode-magic');
      } else {
        wrapper.classList.remove('dark-mode-magic');
      }
    }
  });
};

/**
 * Watch for changes to magic dark mode setting and apply/remove class on #wpbody-content
 */
watch(
  shouldUseMagicDarkMode,
  (enabled) => {
    applyMagicDarkModeClass();
  },
  { immediate: true }
);

/**
 * Watch for URL changes (navigation) and reapply magic dark mode
 */
watch(
  () => window.location.href,
  () => {
    applyMagicDarkModeClass();
  }
);

/**
 * Watch for settings changes to ensure reactivity
 */
watch(
  () => appStore.state.uixpress_settings?.magic_dark_mode_pages,
  () => {
    applyMagicDarkModeClass();
  },
  { deep: true }
);

// Also apply on mount after a short delay to ensure DOM is ready
onMounted(() => {
  setTimeout(() => {
    applyMagicDarkModeClass();
  }, 100);
});
</script>

<template>
  <div
    class="bg-white dark:bg-zinc-900 fixed top-0 left-0 z-[2] h-screen text-zinc-700 dark:text-zinc-300"
  >
    <component is="style">
      :root{font-size:14px}table.fixed {position:
      static}#wpcontent{margin-left:var(--uixp-menu-width)}#adminmenu,
      #adminmenuback, #wpadminbar{z-index:-1;display:none
      !important}#adminmenuwrap{z-index:2; display:block !important}
      html.wp-toolbar { padding-top: var(--uixp-toolbar-height) !important }
      body.is-fullscreen-mode .block-editor__container{ z-index: 3 !important; }
    </component>
    <component is="style" v-html="returnCustomCSS"></component>

    <!-- Menu with combined classes -->
    <ShadowRoot
      tag="div"
      :adopted-style-sheets="[adoptedStyleSheets]"
      :class="menuContainerClasses"
    >
      <component is="style"> {{ returnCustomCSS }}</component>

      <div class="uipx-normalize fixed top-0 left-0 right-0 z-[999999]">
        <LoadingIndicator :height="3" class="z-[999999]" />
      </div>
      <div :class="menuClasses" class="hidden md:flex">
        <AdminMenu id="uixp-menu" class="" />
      </div>

      <div
        class="fixed right-0 top-0 md:left-[var(--uixp-menu-width)] left-0 z-[2] bg-white dark:bg-zinc-900 uipx-normalize border-zinc-200 dark:border-zinc-700/30"
        :class="[
          prefersDark ? 'dark' : '',
          returnLayout === 'bubble' || returnLayout === 'rounded'
            ? ''
            : 'border-b',
        ]"
      >
        <ToolBar id="uixp-toolbar" />
      </div>

      <template
        v-if="
          Array.isArray(appStore.state.uixpress_settings.external_stylesheets)
        "
        v-for="(style, index) in appStore.state.uixpress_settings
          .external_stylesheets"
      >
        <link
          rel="stylesheet"
          :id="`uix-external-${index}-css`"
          :href="style"
          media="all"
        />
      </template>

      <Notifications />
    </ShadowRoot>
  </div>
</template>

<style>
.uipx-normalize * {
  box-sizing: border-box;
}

body,
html {
  @apply text-zinc-700 dark:text-zinc-300 bg-zinc-50 dark:bg-zinc-950;
}

#wpfooter {
  display: none !important;
}

.uixp-rounded #wpwrap {
  @apply bg-zinc-50 dark:bg-zinc-950;
}

@media (min-width: 768px) {
  .uipc-body #wpcontent {
    @apply rounded-3xl;
    position: absolute;
    /* bottom: 3000px; */
    top: 1px;
    right: 1rem;
    margin: 0 !important;
    left: calc(var(--uixp-menu-width));
    overflow: scroll;
    max-height: calc(100vh - var(--uixp-toolbar-height) - 1rem);
  }
  body.uipc-body:not(.uixpress-custom-page) #wpcontent {
    @apply border border-solid border-zinc-200/50 dark:border-zinc-700/30 bg-white dark:bg-zinc-800/20;
  }
}
</style>
